import { ArrowLeft, Eye, Printer, Usb } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppState } from '../../context/AppStateContext';
import { Badge } from '../common/Badge';
import { PageHeader } from '../common/PageHeader';
import { ui } from '../../styles/ui';
import { printBarcode, printBarcodes } from '../../utils/printBarcode';
import { printProductLabelViaUSB, printProductLabelsViaUSB } from '../../utils/tscPrinter';
import { getVerificationDisplayStatus, VERIFICATION_STATUS_ICON, VERIFICATION_STATUS_LABEL, VERIFICATION_STATUS_TONE } from '../../utils/verificationStatus';

const StatusBadge = ({ product }) => {
  const status = getVerificationDisplayStatus(product);
  const Icon = VERIFICATION_STATUS_ICON[status] || VERIFICATION_STATUS_ICON.default;
  return (
    <Badge tone={VERIFICATION_STATUS_TONE[status]} icon={Icon} spin={status === 'in_progress'}>
      {VERIFICATION_STATUS_LABEL[status]}
    </Badge>
  );
};

export const BatchDetailPage = () => {
  const { batchNo: encodedBatchNo } = useParams();
  const batchNo = decodeURIComponent(encodedBatchNo);
  const navigate = useNavigate();
  const { products, loadProducts } = useAppState();

  const [printingId, setPrintingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkPrinting, setBulkPrinting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);

  useEffect(() => {
    loadProducts(undefined, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const units = useMemo(() => products.filter((p) => p.batchNo === batchNo), [products, batchNo]);
  const sample = units[0];

  const allSelected = units.length > 0 && units.every((u) => selectedIds.has(u._id));
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(units.map((u) => u._id)));
  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectedUnits = useMemo(() => units.filter((u) => selectedIds.has(u._id)), [units, selectedIds]);

  const handleUsbPrint = async (product) => {
    setPrintingId(product._id);
    try {
      await printProductLabelViaUSB(product);
    } catch (err) {
      alert(err.message || 'Failed to print label. Check the printer connection in Settings.');
    } finally {
      setPrintingId(null);
    }
  };

  const handleBulkBrowserPrint = () => printBarcodes(selectedUnits);

  const handleBulkUsbPrint = async () => {
    setBulkPrinting(true);
    setBulkProgress({ done: 0, total: selectedUnits.length });
    try {
      const result = await printProductLabelsViaUSB(selectedUnits, undefined, (done, total) =>
        setBulkProgress({ done, total })
      );
      if (result.failed.length) {
        alert(`Printed ${result.total - result.failed.length} of ${result.total} labels. ${result.failed.length} failed — check the printer connection and retry the remaining ones.`);
      }
    } catch (err) {
      alert(err.message || 'Failed to print labels. Check the printer connection in Settings.');
    } finally {
      setBulkPrinting(false);
      setBulkProgress(null);
    }
  };

  if (!sample) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/admin/products')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back to Products
        </button>
        <div className={`${ui.card} p-8 text-center text-sm text-gray-500 dark:text-gray-400`}>
          No units found for batch "{batchNo}" — it may still be loading, or no longer exists.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <button onClick={() => navigate('/admin/products')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
        <ArrowLeft className="h-4 w-4" /> Back to Products
      </button>

      <PageHeader
        title={sample.name}
        subtitle={`Batch ${batchNo} · ${units.length} barcode${units.length === 1 ? '' : 's'} · $${sample.price} per ${sample.unit}`}
      />

      {selectedIds.size > 0 && (
        <div className="rounded-xl border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/60 dark:bg-indigo-500/10 p-3 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm font-medium text-indigo-900 dark:text-indigo-200">
            {selectedIds.size} selected
            {bulkProgress && ` — printing ${bulkProgress.done}/${bulkProgress.total}...`}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkBrowserPrint}
              disabled={bulkPrinting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <Printer className="h-4 w-4" /> Print via Browser
            </button>
            <button
              onClick={handleBulkUsbPrint}
              disabled={bulkPrinting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <Usb className="h-4 w-4" /> {bulkPrinting ? 'Printing…' : 'Print via USB Printer'}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkPrinting}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-2 disabled:opacity-50"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <div className={`${ui.card} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className={`${ui.tableHeadCell} w-10`}>
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} aria-label="Select all" className="rounded border-gray-300 dark:border-gray-600" />
                </th>
                <th className={ui.tableHeadCell}>Barcode</th>
                <th className={ui.tableHeadCell}>Status</th>
                <th className={`${ui.tableHeadCell} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {units.map((unit) => (
                <tr key={unit._id} className={ui.tableRow}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(unit._id)}
                      onChange={() => toggleSelectOne(unit._id)}
                      aria-label={`Select ${unit.barcode}`}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <code className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono font-semibold">{unit.barcode}</code>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <StatusBadge product={unit} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => navigate(`/admin/products/${unit._id}`)} className="p-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400" title="View Details">
                        <Eye className="h-[18px] w-[18px]" />
                      </button>
                      <button onClick={() => printBarcode(unit)} className="p-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white" title="Print Barcode (browser)">
                        <Printer className="h-[18px] w-[18px]" />
                      </button>
                      <button onClick={() => handleUsbPrint(unit)} disabled={printingId === unit._id} className="p-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white disabled:opacity-50" title="Print via TSC USB Printer">
                        <Usb className="h-[18px] w-[18px]" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
